import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Wallet, Plus, Trash2, ArrowDownLeft, ArrowUpRight, RefreshCw, AlertTriangle, X, Building2, Box, ArrowRight, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPrice as formatINR } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Bank Transfer', 'Cash', 'UPI', 'Cheque'];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Partners() {
  const [summary, setSummary] = useState(null);
  const [movements, setMovements] = useState([]);
  const [partnerShare, setPartnerShare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ partner_user_id: '', type: 'investment', amount: '', date: todayISO(), payment_method: 'Bank Transfer', reference_number: '', notes: '' });
  const [filterPartner, setFilterPartner] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [summaryRes, mvtRes, shareRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/partners/summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/partners/movements`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/partners/share`, { withCredentials: true }),
      ]);
      setSummary(summaryRes.data);
      setMovements(mvtRes.data);
      setPartnerShare(shareRes.data.partner_share);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load partners data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => {
    setForm({ partner_user_id: partnerShare[0]?.user_id || '', type: 'investment', amount: '', date: todayISO(), payment_method: 'Bank Transfer', reference_number: '', notes: '' });
    setAddOpen(true);
  };

  const submitMovement = async (e) => {
    e?.preventDefault?.();
    const partner = partnerShare.find(p => p.user_id === form.partner_user_id);
    if (!partner) { toast.error('Pick a partner'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a positive amount'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/partners/movements`, {
        partner_user_id: partner.user_id,
        partner_name: partner.name,
        type: form.type,
        amount: Number(form.amount),
        date: form.date,
        payment_method: form.payment_method,
        reference_number: form.reference_number,
        notes: form.notes,
      }, { withCredentials: true });
      toast.success(`${form.type === 'investment' ? 'Investment' : 'Drawing'} of ₹${formatINR(form.amount)} recorded`);
      setAddOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save movement');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMovement = async (m) => {
    if (!window.confirm(`Delete ${m.type} of ₹${formatINR(m.amount)} on ${m.date}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/partners/movements/${m.capital_movement_id}`, { withCredentials: true });
      toast.success('Movement deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filteredMovements = useMemo(() => {
    return movements.filter(m => {
      if (filterPartner !== 'ALL' && m.partner_user_id !== filterPartner) return false;
      if (filterType !== 'ALL' && m.type !== filterType) return false;
      return true;
    });
  }, [movements, filterPartner, filterType]);

  if (loading) return <div className="text-stone-600">Loading partners ledger...</div>;
  if (!summary) return <div className="text-red-600">Failed to load summary.</div>;
  const pos = summary.business_position;
  const cb = pos.cash_breakdown;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <Wallet className="w-6 h-6 text-amber-700" /> Partner Ledger
          </h1>
          <p className="text-xs text-stone-500 mt-1">Capital contributions, drawings & business net position</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="partners-refresh" variant="outline" size="sm" onClick={fetchAll} className="text-xs h-8 border-stone-200">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button data-testid="partners-add-movement" size="sm" onClick={openAdd} className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8">
            <Plus className="w-3 h-3 mr-1" /> Add Movement
          </Button>
        </div>
      </div>

      {/* Business Net Position panel */}
      <div data-testid="business-net-position" className="rounded-2xl border border-stone-200 bg-gradient-to-br from-amber-50/40 via-white to-lime-50/30 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50/50 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wide text-stone-700">Business Net Position</span>
          <span className="text-2xl font-bold tabular-nums text-stone-900">&#8377;{formatINR(pos.net_position)}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-stone-200">
          <div className="p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-stone-500">
              <ArrowLeftRight className="w-3 h-3" /> Cash in Business
            </div>
            <div data-testid="position-cash" className="text-xl font-bold tabular-nums text-stone-900 mt-1">&#8377;{formatINR(pos.cash)}</div>
            <div className="mt-2 space-y-0.5 text-[10px] text-stone-500">
              <div className="flex justify-between"><span>↑ Customer payments</span><span className="tabular-nums text-lime-700">+{formatINR(cb.customer_payments_in)}</span></div>
              <div className="flex justify-between"><span>↓ Vendor payments</span><span className="tabular-nums text-red-700">−{formatINR(cb.vendor_payments_out)}</span></div>
              <div className="flex justify-between"><span>↓ Expenses</span><span className="tabular-nums text-red-700">−{formatINR(cb.expenses_out)}</span></div>
              <div className="flex justify-between"><span>↑ Partner infusions</span><span className="tabular-nums text-lime-700">+{formatINR(cb.partner_infusions_in)}</span></div>
              <div className="flex justify-between"><span>↓ Partner drawings</span><span className="tabular-nums text-red-700">−{formatINR(cb.partner_drawings_out)}</span></div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-stone-500">
              <Box className="w-3 h-3" /> Inventory at Cost
            </div>
            <div data-testid="position-inventory" className="text-xl font-bold tabular-nums text-stone-900 mt-1">&#8377;{formatINR(pos.inventory_at_cost)}</div>
            <p className="mt-2 text-[10px] text-stone-500 leading-relaxed">Σ stock × input price (latest catalog snapshot)</p>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-stone-500">
              <ArrowRight className="w-3 h-3" /> Receivables
            </div>
            <div data-testid="position-receivables" className="text-xl font-bold tabular-nums text-lime-700 mt-1">&#8377;{formatINR(pos.receivables)}</div>
            <p className="mt-2 text-[10px] text-stone-500">From customers — already delivered, payment pending</p>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold text-stone-500">
              <ArrowDownLeft className="w-3 h-3" /> Payables
            </div>
            <div data-testid="position-payables" className="text-xl font-bold tabular-nums text-red-700 mt-1">&#8377;{formatINR(pos.payables)}</div>
            <p className="mt-2 text-[10px] text-stone-500">To vendors — received goods, payment pending</p>
          </div>
        </div>
        <div className="px-5 py-2 bg-stone-100 border-t border-stone-200 text-[11px] text-stone-600 flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Cash + Inventory + Receivables − Payables</span>
          <span className="font-mono tabular-nums">
            {formatINR(pos.cash)} + {formatINR(pos.inventory_at_cost)} + {formatINR(pos.receivables)} − {formatINR(pos.payables)} = <strong>&#8377;{formatINR(pos.net_position)}</strong>
          </span>
        </div>
      </div>

      {/* Per-partner cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {summary.partners.map(p => {
          const overdrawn = p.profit_owed < 0;
          const gap = p.invest_gap || 0;
          const gapAbs = Math.abs(gap);
          // gap > 0 → contributed more than fair share (partnership owes them)
          // gap < 0 → contributed less than fair share (they owe the partnership)
          const showGap = summary.total_invested > 0 && gapAbs > 0.5;
          return (
            <div key={p.user_id} data-testid={`partner-card-${p.user_id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between border-b border-stone-100 pb-2 mb-3">
                <div>
                  <div className="text-base font-bold text-stone-900">{p.name}</div>
                  <div className="text-[10px] text-stone-500 uppercase tracking-wide font-semibold flex items-center gap-2">
                    <span className="text-amber-700">Profit: {p.profit_share_percent}%</span>
                    <span className="text-stone-300">·</span>
                    <span className="text-blue-700">Invest: {p.investment_share_percent}%</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-stone-400">Net Capital</div>
                  <div className="text-xl font-bold tabular-nums text-stone-900">&#8377;{formatINR(p.net_capital)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-lime-50/60 border border-lime-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wide text-lime-700 font-semibold">Invested</div>
                  <div className="text-base font-bold tabular-nums text-lime-800">&#8377;{formatINR(p.invested)}</div>
                  <div className="text-[9px] text-stone-500 mt-0.5">Expected (per {p.investment_share_percent}%): &#8377;{formatINR(p.expected_invested)}</div>
                </div>
                <div className="bg-red-50/60 border border-red-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wide text-red-700 font-semibold">Drawn</div>
                  <div className="text-base font-bold tabular-nums text-red-800">&#8377;{formatINR(p.drawn)}</div>
                </div>
              </div>
              {showGap && (
                <div className={`mt-2 text-[11px] flex items-center gap-1.5 px-2 py-1.5 rounded border ${gap > 0 ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                  <span>
                    {gap > 0
                      ? <>Contributed <strong>&#8377;{formatINR(gapAbs)}</strong> more than {p.investment_share_percent}% share — partnership owes them</>
                      : <>Owes the partnership <strong>&#8377;{formatINR(gapAbs)}</strong> to restore the {p.investment_share_percent}% investment ratio</>
                    }
                  </span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-stone-100">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-stone-500">Their share of net profit ({p.profit_share_percent}%)</span>
                  <span className="tabular-nums font-semibold text-stone-700">&#8377;{formatINR(p.profit_share)}</span>
                </div>
                <div className={`flex items-center justify-between mt-1.5 text-xs ${overdrawn ? 'text-red-700' : 'text-lime-700'}`}>
                  <span className="font-medium">{overdrawn ? 'Over-drawn by' : 'Profit balance still owed'}</span>
                  <span className="tabular-nums font-bold">
                    {overdrawn && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    &#8377;{formatINR(Math.abs(p.profit_owed))}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-stone-500 italic px-1">
        Net profit since inception: <strong className="text-stone-700">&#8377;{formatINR(summary.net_profit_since_inception)}</strong> (representational — drawings do NOT affect operational Net Profit on Financials page)
      </div>

      {/* Movements filters + table */}
      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-stone-200 bg-stone-50/50">
          <span className="text-xs font-bold uppercase tracking-wide text-stone-700">Capital Movements ({filteredMovements.length})</span>
          <div className="flex items-center gap-2">
            <Select value={filterPartner} onValueChange={setFilterPartner}>
              <SelectTrigger data-testid="filter-partner" className="h-7 text-xs border-stone-200 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All partners</SelectItem>
                {partnerShare.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="filter-type" className="h-7 text-xs border-stone-200 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All types</SelectItem>
                <SelectItem value="investment">Investment</SelectItem>
                <SelectItem value="drawing">Drawing</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {filteredMovements.length === 0 ? (
          <div className="p-8 text-center text-xs text-stone-500">No movements recorded yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-2.5 font-semibold text-stone-600">Date</th>
                <th className="text-left p-2.5 font-semibold text-stone-600">Partner</th>
                <th className="text-left p-2.5 font-semibold text-stone-600">Type</th>
                <th className="text-right p-2.5 font-semibold text-stone-600">Amount</th>
                <th className="text-left p-2.5 font-semibold text-stone-600">Method</th>
                <th className="text-left p-2.5 font-semibold text-stone-600">Reference</th>
                <th className="text-left p-2.5 font-semibold text-stone-600">Notes</th>
                <th className="text-right p-2.5 font-semibold text-stone-600">Recorded by</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.map(m => {
                const isInv = m.type === 'investment';
                return (
                  <tr key={m.capital_movement_id} data-testid={`movement-${m.capital_movement_id}`} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="p-2.5 tabular-nums text-stone-700">{m.date}</td>
                    <td className="p-2.5 font-semibold text-stone-900">{m.partner_name}</td>
                    <td className="p-2.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${isInv ? 'bg-lime-50 text-lime-800 border border-lime-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        {isInv ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                        {isInv ? 'Investment' : 'Drawing'}
                      </span>
                    </td>
                    <td className={`p-2.5 text-right tabular-nums font-bold ${isInv ? 'text-lime-700' : 'text-red-700'}`}>
                      {isInv ? '+' : '−'}&#8377;{formatINR(m.amount)}
                    </td>
                    <td className="p-2.5 text-stone-600">{m.payment_method}</td>
                    <td className="p-2.5 text-stone-600 font-mono text-[10px]">{m.reference_number || '—'}</td>
                    <td className="p-2.5 text-stone-600 max-w-xs truncate">{m.notes || '—'}</td>
                    <td className="p-2.5 text-right text-[10px] text-stone-400">{m.created_by_name}</td>
                    <td className="p-2.5">
                      <button data-testid={`delete-movement-${m.capital_movement_id}`} onClick={() => deleteMovement(m)} title="Delete" className="p-1 rounded text-stone-400 hover:text-red-700 hover:bg-red-50 transition">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Movement dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Capital Movement</DialogTitle>
            <p className="text-[11px] text-stone-500">Record an investment in or a drawing out of the business.</p>
          </DialogHeader>
          <form onSubmit={submitMovement} className="space-y-3">
            <div>
              <Label className="text-xs">Partner *</Label>
              <Select value={form.partner_user_id} onValueChange={(v) => setForm(f => ({ ...f, partner_user_id: v }))}>
                <SelectTrigger data-testid="form-partner" className="border-stone-200 h-9"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>
                  {partnerShare.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} (Profit {p.profit_share_percent}% · Invest {p.investment_share_percent}%)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="form-type" className="border-stone-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="investment">Investment (money in)</SelectItem>
                    <SelectItem value="drawing">Drawing (money out)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount (₹) *</Label>
                <Input data-testid="form-amount" type="number" min="0" step="any" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g., 50000" required className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">Date *</Label>
                <Input data-testid="form-date" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm(f => ({ ...f, payment_method: v }))}>
                  <SelectTrigger data-testid="form-method" className="border-stone-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Reference number (optional)</Label>
              <Input data-testid="form-ref" value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} placeholder="Bank txn ref / cheque #" className="border-stone-200 h-9" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <textarea
                data-testid="form-notes"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                maxLength={1000}
                placeholder="e.g., Working capital top-up for June, May drawing"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <Button type="button" variant="outline" size="sm" onClick={() => setAddOpen(false)} className="text-xs h-9">Cancel</Button>
              <Button data-testid="form-submit" type="submit" size="sm" disabled={submitting} className={`text-xs h-9 ${form.type === 'investment' ? 'bg-lime-700 hover:bg-lime-800' : 'bg-red-700 hover:bg-red-800'} text-white`}>
                {submitting ? 'Saving…' : (form.type === 'investment' ? 'Record Investment' : 'Record Drawing')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
